import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { configService } from '../services/ConfigService';
import '../styles/EmailManagement.css';

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  enabled: boolean;
  description: string;
  variables: string[];
}

function EmailManagement() {
  const navigate = useNavigate();
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([
    {
      id: 'booking_confirmation',
      name: 'تأكيد الحجز',
      subject: 'تأكيد الحجز - {bookingCode}',
      enabled: true,
      description: 'يُرسل عند إنشاء حجز جديد',
      variables: ['customerName', 'bookingCode', 'boothType', 'eventDate', 'eventTime', 'location']
    },
    {
      id: 'tickets_ready',
      name: 'التذاكر جاهزة',
      subject: 'تذاكرك جاهزة - {bookingCode}',
      enabled: true,
      description: 'يُرسل بعد إتمام الدفع وإنشاء التذاكر',
      variables: ['customerName', 'bookingCode', 'ticketCount', 'viewTicketsUrl']
    },
    {
      id: 'event_reminder',
      name: 'تذكير بالمناسبة',
      subject: 'تذكير - موعد حجزك غداً',
      enabled: true,
      description: 'يُرسل قبل يوم من المناسبة',
      variables: ['customerName', 'eventDate', 'eventTime', 'location', 'bookingCode']
    }
  ]);

  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<boolean>(false);
  const [templateContent, setTemplateContent] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  // Admin notifications state
  const [adminEmails, setAdminEmails] = useState<string[]>([]);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [adminNotificationsLoading, setAdminNotificationsLoading] = useState(false);

  // Invoice attachment state
  const [attachInvoiceWithConfirmation, setAttachInvoiceWithConfirmation] = useState(true);

  // Auto reminder state
  const [autoReminder, setAutoReminder] = useState(false);

  useEffect(() => {
    // التحقق من صلاحية الوصول
    const token = localStorage.getItem('fotomista_token');
    if (!token) {
      navigate('/admin-login');
    }
    
    // جلب إعدادات القوالب من الخادم
    fetchEmailSettings();

    // جلب إعدادات إشعارات الإدارة
    loadAdminEmailsAndSettings();
  }, [navigate]);

  const fetchEmailSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.EMAIL_SETTINGS}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('fotomista_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // تحديث القوالب بالبيانات من الخادم
        if (data.templates) {
          setEmailTemplates(data.templates);
        }
        if (data.content) {
          setTemplateContent(data.content);
        }
        if (data.attachInvoiceWithConfirmation !== undefined) {
          setAttachInvoiceWithConfirmation(data.attachInvoiceWithConfirmation);
        }
      }
    } catch (error) {
      console.error('Error fetching email settings:', error);
    }
  };

  const loadAdminEmailsAndSettings = async () => {
    try {
      // التأكد من تحميل الإعدادات أولاً
      await configService.initialize();
      // الانتظار قليلاً للتأكد من اكتمال التحميل
      await new Promise(resolve => setTimeout(resolve, 300));
      // جلب الإيميلات بعد التأكد من اكتمال التحميل
      const emails = configService.getAdminNotificationEmails();
      console.log('Loaded admin emails:', emails);
      setAdminEmails(emails);
      
      const config = configService.getConfig();
      if (config.depositSettings) {
        setAutoReminder(config.depositSettings.autoReminder);
      }
    } catch (error) {
      console.error('Error loading admin emails or settings:', error);
    }
  };

  const handleAddAdminEmail = async () => {
    if (!newAdminEmail.trim()) {
      alert('يرجى إدخال عنوان بريد إلكتروني صحيح');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newAdminEmail)) {
      alert('عنوان البريد الإلكتروني غير صحيح');
      return;
    }

    if (adminEmails.includes(newAdminEmail)) {
      alert('هذا البريد الإلكتروني موجود بالفعل');
      return;
    }

    setAdminNotificationsLoading(true);
    try {
      const updatedEmails = [...adminEmails, newAdminEmail];
      await configService.updateAdminNotificationEmails(updatedEmails);
      setAdminEmails(updatedEmails);
      setNewAdminEmail('');
      alert('تم إضافة البريد الإلكتروني بنجاح');
    } catch (error) {
      console.error('Error adding admin email:', error);
      alert('حدث خطأ في إضافة البريد الإلكتروني');
    } finally {
      setAdminNotificationsLoading(false);
    }
  };

  const handleRemoveAdminEmail = async (emailToRemove: string) => {
    setAdminNotificationsLoading(true);
    try {
      const updatedEmails = adminEmails.filter(email => email !== emailToRemove);
      await configService.updateAdminNotificationEmails(updatedEmails);
      setAdminEmails(updatedEmails);
      alert('تم حذف البريد الإلكتروني بنجاح');
    } catch (error) {
      console.error('Error removing admin email:', error);
      alert('حدث خطأ في حذف البريد الإلكتروني');
    } finally {
      setAdminNotificationsLoading(false);
    }
  };

  const handleSendTestAdminNotification = async () => {
    if (adminEmails.length === 0) {
      alert('يرجى إضافة بريد إلكتروني واحد على الأقل');
      return;
    }

    setAdminNotificationsLoading(true);
    try {
      await configService.sendTestAdminNotification();
      alert(`تم إرسال الإشعار التجريبي إلى ${adminEmails.length} عنوان بريد إلكتروني`);
    } catch (error) {
      console.error('Error sending test notification:', error);
      alert('حدث خطأ في إرسال الإشعار التجريبي');
    } finally {
      setAdminNotificationsLoading(false);
    }
  };

  const handleToggleTemplate = async (templateId: string) => {
    setLoading(true);
    try {
      const template = emailTemplates.find(t => t.id === templateId);
      if (!template) return;

      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.EMAIL_TOGGLE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('fotomista_token')}`
        },
        body: JSON.stringify({
          templateId,
          enabled: !template.enabled
        })
      });

      if (response.ok) {
        setEmailTemplates(prev =>
          prev.map(t =>
            t.id === templateId ? { ...t, enabled: !t.enabled } : t
          )
        );
      }
    } catch (error) {
      console.error('Error toggling template:', error);
      alert('حدث خطأ في تحديث الإعدادات');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContent = async (templateId: string) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.EMAIL_CONTENT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('fotomista_token')}`
        },
        body: JSON.stringify({
          templateId,
          content: templateContent[templateId]
        })
      });

      if (response.ok) {
        setEditingTemplate(null);
        alert('تم حفظ المحتوى بنجاح');
      } else {
        alert('حدث خطأ في حفظ المحتوى');
      }
    } catch (error) {
      console.error('Error saving content:', error);
      alert('حدث خطأ في حفظ المحتوى');
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestEmail = async (templateId: string) => {
    if (!testEmail) {
      alert('يرجى إدخال البريد الإلكتروني');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.EMAIL_TEST_TEMPLATE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('fotomista_token')}`
        },
        body: JSON.stringify({
          templateId,
          email: testEmail
        })
      });

      if (response.ok) {
        alert('تم إرسال البريد التجريبي بنجاح');
        setTestEmail('');
      } else {
        alert('فشل إرسال البريد التجريبي');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      alert('حدث خطأ في إرسال البريد');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAttachInvoice = async () => {
    setLoading(true);
    try {
      const newState = !attachInvoiceWithConfirmation;
      const response = await fetch(`${API_BASE_URL}/api/email/settings/attach-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('fotomista_token')}`
        },
        body: JSON.stringify({ enabled: newState })
      });

      if (response.ok) {
        setAttachInvoiceWithConfirmation(newState);
      } else {
        alert('حدث خطأ في حفظ إعداد الفاتورة');
      }
    } catch (error) {
      console.error('Error toggling invoice attachment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAutoReminder = async () => {
    setLoading(true);
    try {
      const newState = !autoReminder;
      const config = configService.getConfig();
      const depositSettings = config.depositSettings || { enabled: false, type: 'percentage', percentage: 50, amount: 100, autoReminder: false };
      
      const newConfig = {
        ...config,
        depositSettings: {
          ...depositSettings,
          autoReminder: newState
        }
      };
      
      await configService.saveConfig(newConfig);
      setAutoReminder(newState);
    } catch (error) {
      console.error('Error toggling auto reminder:', error);
      alert('حدث خطأ في حفظ إعدادات التذكير التلقائي');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title="إدارة البريد الإلكتروني">
      <motion.div 
        className="email-overview"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="email-management-container">
        <div className="templates-grid">
          {emailTemplates.map(template => (
            <div key={template.id} className="template-card">
              <div className="template-header">
                <h3>{template.name}</h3>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={template.enabled}
                    onChange={() => handleToggleTemplate(template.id)}
                    disabled={loading}
                  />
                  <span className="slider"></span>
                </label>
              </div>

              <p className="template-description">{template.description}</p>
              
              <div className="template-subject">
                <strong>العنوان:</strong> {template.subject}
              </div>

              <div className="template-variables">
                <strong>المتغيرات المتاحة:</strong>
                <div className="variables-list">
                  {template.variables.map(variable => (
                    <span key={variable} className="variable-tag">
                      {`{${variable}}`}
                    </span>
                  ))}
                </div>
              </div>

              <div className="template-actions">
                {editingTemplate === template.id ? (
                  <div className="edit-content">
                    <div className="editor-tabs" style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                      <button 
                        onClick={() => setPreviewMode(false)}
                        className={`tab-btn ${!previewMode ? 'active' : ''}`}
                        style={{ padding: '8px 16px', border: 'none', borderRadius: '4px', cursor: 'pointer', background: !previewMode ? '#3A5F5F' : '#eee', color: !previewMode ? 'white' : '#333' }}
                      >تعديل الكود HTML</button>
                      <button 
                        onClick={() => setPreviewMode(true)}
                        className={`tab-btn ${previewMode ? 'active' : ''}`}
                        style={{ padding: '8px 16px', border: 'none', borderRadius: '4px', cursor: 'pointer', background: previewMode ? '#3A5F5F' : '#eee', color: previewMode ? 'white' : '#333' }}
                      >معاينة القالب</button>
                    </div>
                    
                    {!previewMode ? (
                      <textarea
                        className="content-editor"
                        value={templateContent[template.id] || ''}
                        onChange={(e) => setTemplateContent({
                          ...templateContent,
                          [template.id]: e.target.value
                        })}
                        placeholder="اكتب محتوى القالب هنا..."
                        rows={10}
                      />
                    ) : (
                      <div className="content-preview" style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '10px', minHeight: '300px', backgroundColor: '#fff', overflow: 'auto' }}>
                        <iframe 
                          srcDoc={templateContent[template.id] || ''} 
                          style={{ width: '100%', height: '400px', border: 'none' }}
                          title="Email Preview"
                        />
                      </div>
                    )}
                    
                    <div className="edit-buttons" style={{ marginTop: '15px' }}>
                      <button 
                        onClick={() => handleSaveContent(template.id)}
                        disabled={loading}
                        className="save-btn"
                      >
                        حفظ
                      </button>
                      <button 
                        onClick={() => {
                          setEditingTemplate(null);
                          setPreviewMode(false);
                        }}
                        className="cancel-btn"
                      >
                        إلغاء
                      </button>
                    </div>
                  </div>
                ) : (
                  <button 
                    onClick={() => setEditingTemplate(template.id)}
                    className="edit-btn"
                  >
                    تحرير المحتوى
                  </button>
                )}

                <div className="test-email-section">
                  <input
                    type="email"
                    placeholder="البريد الإلكتروني للاختبار"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="test-email-input"
                  />
                  <button 
                    onClick={() => handleSendTestEmail(template.id)}
                    disabled={loading || !testEmail}
                    className="test-btn"
                  >
                    إرسال اختبار
                  </button>
                </div>
              </div>
            </div>
          ))}
          
          {/* Auto Reminder Card (Treated like a template but tied to depositSettings) */}
          <div className="template-card">
            <div className="template-header">
              <h3>تذكير بدفع المبلغ المتبقي</h3>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={autoReminder}
                  onChange={handleToggleAutoReminder}
                  disabled={loading}
                />
                <span className="slider"></span>
              </label>
            </div>

            <p className="template-description">يُرسل تلقائياً قبل يوم من المناسبة لتذكير العميل بدفع المبلغ المتبقي (إن وُجد) مع رابط الدفع.</p>
            
            <div className="template-subject">
              <strong>ملاحظة:</strong> هذا القالب يتم إدراجه تلقائياً مع "تذكير بالمناسبة" ولا يمكن تعديل نصه لتجنب الأخطاء البرمجية في الروابط والمبالغ.
            </div>

            <div className="template-variables" style={{ marginTop: '15px' }}>
              <strong>المتغيرات المضمنة:</strong>
              <div className="variables-list">
                <span className="variable-tag">{`{customerName}`}</span>
                <span className="variable-tag">{`{bookingCode}`}</span>
                <span className="variable-tag">{`{remainingAmount}`}</span>
                <span className="variable-tag">{`{paymentLink}`}</span>
              </div>
            </div>

            <div className="template-actions" style={{ marginTop: '20px' }}>
              <div className="test-email-section">
                <input
                  type="email"
                  placeholder="البريد الإلكتروني للاختبار"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="test-email-input"
                />
                <button 
                  onClick={() => handleSendTestEmail('deposit_reminder')}
                  disabled={loading || !testEmail}
                  className="test-btn"
                >
                  إرسال اختبار
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="reminder-settings" style={{ marginTop: '30px' }}>
          <h2>إعدادات مرفقات الفاتورة (PDF)</h2>
          <div className="reminder-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="reminder-info">
              <p style={{ fontWeight: 'bold' }}>إرفاق الفاتورة مع إيميل تأكيد الحجز</p>
              <p>عند تفعيل هذا الخيار، سيتم إنشاء الفاتورة تلقائياً كملف PDF وإرفاقه مع رسالة "تأكيد الحجز".</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={attachInvoiceWithConfirmation}
                onChange={handleToggleAttachInvoice}
                disabled={loading}
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>

        {/* Admin Notifications Section */}
        <div className="admin-notifications-section">
          <h2>إشعارات الإدارة</h2>
          <div className="admin-notifications-card">
            <div className="admin-notifications-info">
              <p>يتم إرسال إشعارات للإدارة عند:</p>
              <ul>
                <li>إنشاء حجز جديد (مع تفاصيل العميل والحجز والدفع)</li>
                <li>شراء تذاكر إضافية (مع تفاصيل الشراء)</li>
              </ul>
            </div>

            <div className="admin-emails-management">
              <h3>إيميلات الإدارة</h3>

              <div className="add-email-section">
                <input
                  type="email"
                  placeholder="أدخل بريد إلكتروني جديد"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  className="email-input"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddAdminEmail()}
                />
                <button
                  onClick={handleAddAdminEmail}
                  disabled={adminNotificationsLoading || !newAdminEmail.trim()}
                  className="add-email-btn"
                >
                  إضافة
                </button>
              </div>

              <div className="emails-list">
                {adminEmails.length === 0 ? (
                  <p className="no-emails">لا توجد إيميلات مضافة بعد</p>
                ) : (
                  adminEmails.map((email, index) => (
                    <div key={index} className="email-item">
                      <span className="email-address">{email}</span>
                      <button
                        onClick={() => handleRemoveAdminEmail(email)}
                        disabled={adminNotificationsLoading}
                        className="remove-email-btn"
                        title="حذف"
                      >
                        ✕
                      </button>
                    </div>
                  ))
                )}
              </div>

              <div className="test-notification-section">
                <button
                  onClick={handleSendTestAdminNotification}
                  disabled={adminNotificationsLoading || adminEmails.length === 0}
                  className="test-notification-btn"
                >
                  {adminNotificationsLoading ? 'جاري الإرسال...' : 'إرسال إشعار تجريبي'}
                </button>
                {adminEmails.length > 0 && (
                  <p className="test-info">
                    سيتم إرسال الإشعار التجريبي إلى {adminEmails.length} عنوان بريد إلكتروني
                  </p>
                )}
              </div>

              <div className="notification-status">
                <span className={`status-indicator ${adminEmails.length > 0 ? 'active' : 'inactive'}`}></span>
                <span>
                  {adminEmails.length > 0
                    ? `إشعارات الإدارة مفعّلة (${adminEmails.length} إيميل)`
                    : 'إشعارات الإدارة غير مفعّلة'
                  }
                </span>
              </div>
            </div>
          </div>
        </div>
        </div>
      </motion.div>
    </AdminLayout>
  );
}

export default EmailManagement;